import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, DoorClosed, CheckCircle2, AlertTriangle, Ban, HelpCircle } from "lucide-react";
import { buildingsApi, doorsApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

function statusMeta(s) {
  const k = String(s || "").toLowerCase();
  if (k === "pass") return { cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, label: "Satisfactory" };
  if (k === "fail") return { cls: "bg-red-100 text-red-700", Icon: AlertTriangle, label: "Remedial action required" };
  if (k === "review") return { cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle, label: "Further review required" };
  if (k === "no_access") return { cls: "bg-slate-200 text-slate-700", Icon: Ban, label: "No access" };
  return { cls: "bg-slate-100 text-slate-500", Icon: HelpCircle, label: "Not inspected" };
}

export default function PortalBuildingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const building = useAsync(() => buildingsApi.getOne(id), [id]);
  const doors = useAsync(() => doorsApi.getAll(id), [id]);
  const b = building.data;
  const doorList = Array.isArray(doors.data) ? doors.data : [];

  const summary = useMemo(() => {
    const acc = { pass: 0, fail: 0, review: 0, other: 0 };
    doorList.forEach((d) => {
      const k = String(d.last_inspection_status || "").toLowerCase();
      if (k === "pass") acc.pass++; else if (k === "fail") acc.fail++; else if (k === "review") acc.review++; else acc.other++;
    });
    return acc;
  }, [doorList]);

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => navigate("/portal/buildings")} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All buildings
      </button>
      {building.loading && <LoadingState label="Loading building…" />}
      {!building.loading && building.error && <ErrorState message={building.error} onRetry={building.reload} />}
      {b && (
        <>
          <PageHeader title={b.name} subtitle={<span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[b.address, b.postcode].filter(Boolean).join(", ")}</span>} testId="portal-building-detail-header" />
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="p-4"><p className="text-xs text-slate-400">Doors</p><p className="text-2xl font-bold text-slate-900">{doorList.length}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400">Satisfactory</p><p className="text-2xl font-bold text-emerald-600">{summary.pass}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400">Needs attention</p><p className="text-2xl font-bold text-red-600">{summary.fail}</p></Card>
            <Card className="p-4"><p className="text-xs text-slate-400">Not inspected</p><p className="text-2xl font-bold text-slate-500">{summary.other}</p></Card>
          </div>

          <h3 className="mb-2 font-semibold text-slate-800">Doors</h3>
          {doors.loading && <LoadingState label="Loading doors…" rows={2} />}
          {!doors.loading && doorList.length === 0 && <EmptyState title="No doors" message="No doors are on the register for this building yet." />}
          {!doors.loading && doorList.length > 0 && (
            <div className="space-y-2">
              {doorList.map((d) => {
                const m = statusMeta(d.last_inspection_status);
                return (
                  <Card key={d.id} className="flex items-center justify-between gap-3 p-3.5" data-testid={`portal-door-${d.id}`}>
                    <div className="flex min-w-0 items-center gap-2">
                      <DoorClosed className="h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">{d.door_reference || "Door"}</p>
                        {d.location ? <p className="truncate text-xs text-slate-400">{d.location}</p> : null}
                      </div>
                    </div>
                    <Badge className={m.cls}><m.Icon className="mr-1 h-3.5 w-3.5" />{m.label}</Badge>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
